import React, { useState, useEffect } from "react";
import { 
  Shield, 
  Award, 
  Calendar, 
  CheckCircle2, 
  Users, 
  Star, 
  BookOpen, 
  ChevronRight, 
  Phone, 
  Mail, 
  Clock, 
  ArrowRight, 
  Check, 
  X, 
  AlertTriangle, 
  Lightbulb, 
  GraduationCap, 
  TrendingUp, 
  Map, 
  Sparkles, 
  Heart, 
  Target 
} from "lucide-react";
import "@/App.css";

export default function App() {
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    academicStatus: ""
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Monitor scroll for header background opacity
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openModal = (tierName = "General Inquiry") => {
    setSelectedTier(tierName);
    setShowLeadModal(true);
    setIsSubmitted(false);
  };

  const closeModal = () => {
    setShowLeadModal(false);
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      academicStatus: ""
    });
    setErrorMsg("");
    setIsSubmitted(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.phone || !formData.academicStatus) {
      setErrorMsg("Please fill in all fields.");
      return;
    }
    setErrorMsg("");
    
    // Generate a beautiful unique Ticket ID
    const randomNum = Math.floor(100000 + Math.random() * 900000);
    setTicketId(`MC-${randomNum}`);
    setIsSubmitted(true);
    
    // Save to local storage for persistent mock state
    const existingLeads = JSON.parse(localStorage.getItem("mc_leads") || "[]");
    existingLeads.push({
      id: `MC-${randomNum}`,
      ...formData,
      tier: selectedTier,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem("mc_leads", JSON.stringify(existingLeads));
  };

  return (
    <div className="min-h-screen bg-[#0A0F17] text-[#F9FAFB] font-body flex flex-col selection:bg-[#D4AF37] selection:text-[#0A0F17] overflow-x-hidden">
      
      {/* HEADER / NAVIGATION */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? "bg-[#0A0F17]/95 backdrop-blur-md py-4 border-b border-white/10 shadow-lg" : "bg-transparent py-6"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <div className="flex items-center -space-x-1 sm:-space-x-1.5 flex-shrink-0">
              <img src="https://tagmango.com/assets/edge-quest-global-refined-image-28-jul-2026-1c40d2793d3f96d8da721cd73e63cc91.png" alt="EdgeQuest Global Logo" className="h-8 w-8 sm:h-9 sm:w-9 object-contain rounded-full border border-[#D4AF37]/20 bg-black" />
              <img src="https://tagmango.com/assets/logo-2-for-future-officers-hub-da2408e6deeee9ec83e69c316872df57.png" alt="Future Officers Hub Logo" className="h-8 w-8 sm:h-9 sm:w-9 object-contain rounded-full border border-[#D4AF37]/20 bg-black" />
            </div>
            <div>
              <span className="font-heading font-black text-lg sm:text-2xl tracking-wider text-white">
                MISSION COMMISSION<span className="text-[#D4AF37]">™</span>
              </span>
              <p className="text-[8px] sm:text-[10px] uppercase tracking-[0.08em] sm:tracking-[0.12em] text-[#D4AF37] font-semibold -mt-1 font-heading">
                powered by EdgeQuest Global and Future Officers Hub
              </p>
            </div>
          </div>
          
          <nav className="hidden lg:flex items-center space-x-8">
            <a href="#problem" data-testid="nav-link-problem" className="text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors">The Problem</a>
            <a href="#roadmap" data-testid="nav-link-roadmap" className="text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors">5-Phase Roadmap</a>
            <a href="#officer-path" data-testid="nav-link-officer-path" className="text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors">The OFFICER Path</a>
            <a href="#offers" data-testid="nav-link-three-level-offer" className="text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors">3-Level Offer</a>
            <a href="#mentor" data-testid="nav-link-mentor-bio" className="text-sm font-medium text-gray-300 hover:text-[#D4AF37] transition-colors">Mentor Bio</a>
          </nav>

          <div>
            <button 
              onClick={() => openModal("General Book Seat")}
              data-testid="nav-link-cta-book-seat"
              className="px-5 py-2 sm:px-6 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold bg-[#D4AF37] text-[#0A0F17] hover:bg-[#E5BE3E] transition-all transform hover:scale-105 active:scale-95 duration-200 shadow-md shadow-[#D4AF37]/20 font-subheading flex items-center gap-1.5"
            >
              Book My Seat
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-32 flex items-center overflow-hidden">
        {/* Background glow effects */}
        <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/5 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-[400px] h-[400px] rounded-full bg-blue-500/5 blur-[100px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Hero Left Content */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-xs font-bold tracking-[0.1em] text-[#D4AF37] font-heading uppercase">
                <Star className="h-3 w-3 fill-[#D4AF37] text-[#D4AF37]" />
                <span>Exclusive Guidance for Class 11, 12 & Recent Graduates</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight font-heading uppercase">
                Your Dream of Wearing the Uniform Needs the <span className="gold-text-gradient">Right Path.</span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-white/90 font-medium font-subheading max-w-2xl border-l-4 border-[#D4AF37] pl-4">
                Discover Your Commission Route After 12th Standard
              </p>
              
              <p className="text-base sm:text-lg text-gray-300 font-body leading-relaxed max-w-2xl">
                Understand your officer-entry opportunities, identify the right pathway, know the eligibility and examination requirements, and start developing the mind, body, character and leadership potential of a future officer—early.
              </p>
              
              <div className="py-4 border-t border-b border-white/10 max-w-xl">
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold mb-2 font-heading">
                  From Dream to Commission™ Matrix
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-200">
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#D4AF37]" /> Career Clarity</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#D4AF37]" /> Written Preparation</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#D4AF37]" /> Physical Fitness</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#D4AF37]" /> SSB Readiness</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  onClick={() => openModal("Hero Primary CTA")}
                  data-testid="hero-primary-cta"
                  className="px-8 py-4 rounded-xl text-sm sm:text-base font-bold bg-[#D4AF37] text-[#0A0F17] hover:bg-[#E5BE3E] transition-all transform hover:-translate-y-0.5 active:translate-y-0 duration-200 shadow-lg shadow-[#D4AF37]/35 font-subheading uppercase tracking-wider flex items-center justify-center gap-2"
                >
                  Discover My Officer Path
                  <ArrowRight className="h-5 w-5" />
                </button>
                <a
                  href="#roadmap"
                  data-testid="hero-secondary-cta"
                  className="px-8 py-4 rounded-xl text-sm sm:text-base font-bold bg-white/5 text-white hover:bg-white/10 transition-all border border-white/10 font-subheading flex items-center justify-center gap-2"
                >
                  Explore Roadmap
                </a>
              </div>
              
              <p className="text-xs text-gray-400 font-medium italic pt-1">
                * Perfect for Class 11, Class 12, and young aspirants planning an officer career in the Indian Armed Forces.
              </p>
            </div>
            
            {/* Hero Right Media (Thumbnail Graphic) */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-[480px] lg:max-w-none rounded-2xl overflow-hidden border border-[#D4AF37]/45 shadow-2xl gold-glow">
                <img 
                  src="https://tagmango.com/assets/from-dream-to-commission-the-officer-path-aa1382b3e1e187c6749c9e1d7b65bf33.png" 
                  alt="From Dream to Commission: The Officer Path Timeline" 
                  data-testid="hero-banner-image"
                  className="w-full h-auto object-cover transform hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F17] via-transparent to-transparent opacity-40 pointer-events-none" />
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-[#1A2234] border border-[#D4AF37]/30 px-5 py-4 rounded-xl shadow-xl hidden sm:flex items-center gap-4 max-w-xs">
                <div className="p-3 bg-[#D4AF37]/10 rounded-lg">
                  <Award className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest font-heading font-bold">Guided By</p>
                  <p className="text-sm font-bold text-white">Col Ravindranath Tiwari</p>
                  <p className="text-[10px] text-[#D4AF37] font-semibold">30+ Years Army Veteran</p>
                </div>
              </div>
            </div>

          </div>

          {/* TRUST METRICS BLOCK */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 pt-16 mt-12 border-t border-white/5 max-w-3xl">
            <div className="bg-[#111827]/60 border border-white/5 rounded-xl p-4 sm:p-6 text-center">
              <p className="text-2xl sm:text-4xl lg:text-5xl font-extrabold font-heading text-[#D4AF37]">30+ Yrs</p>
              <p className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-2 font-medium">Military Leadership</p>
            </div>
            <div className="bg-[#111827]/60 border border-white/5 rounded-xl p-4 sm:p-6 text-center">
              <p className="text-2xl sm:text-4xl lg:text-5xl font-extrabold font-heading text-[#D4AF37]">UPSC</p>
              <p className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-2 font-medium">Undergraduate Aspirants Mentored</p>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 2: THE PROBLEM */}
      <section id="problem" className="py-20 sm:py-28 bg-[#111827] relative border-t border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F17]/0 via-red-500/[0.015] to-[#0A0F17]/0 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          
          <div className="max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold font-heading">The Strategic Gap</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-heading uppercase">
              Don't Wait Until the Exam to <span className="text-[#EF4444]">Discover the Path</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-300">
              Many students dream of joining the Armed Forces but begin with scattered, confusing information. They know about NDA, but miss out on key direct entries, physical criteria, medical parameters, and Officer Like Qualities (OLQs) needed for the SSB.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch max-w-5xl mx-auto text-left">
            
            {/* Wrong Sequence Card */}
            <div className="bg-[#0A0F17] rounded-2xl p-6 sm:p-8 border border-[#EF4444]/25 flex flex-col justify-between">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-[#EF4444]/10 border border-[#EF4444]/20 text-xs font-bold text-[#EF4444] font-heading uppercase">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>The Unprepared Journey (High Risk)</span>
                </div>
                
                <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading">
                  The Scattered Sequence
                </h3>
                <p className="text-sm sm:text-base text-gray-400">
                  Aspirants follow a reactive timeline, relying on luck, unverified YouTube videos, and cramming at the very last minute.
                </p>

                {/* Flow Block */}
                <div className="space-y-3 bg-[#111827]/60 p-4 rounded-xl border border-white/5">
                  <div className="flex items-center space-x-3 text-sm text-gray-300">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-500/10 text-[#EF4444] rounded-full text-xs font-bold">1</span>
                    <span><strong>Dream:</strong> Passion arises at age 16-17.</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-300">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-500/10 text-[#EF4444] rounded-full text-xs font-bold">2</span>
                    <span><strong>Search YouTube:</strong> Get flooded with contradictory guidance.</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-300">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-500/10 text-[#EF4444] rounded-full text-xs font-bold">3</span>
                    <span><strong>Join Coaching:</strong> Rote-learn math formulae & GK booklets.</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-300">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-500/10 text-[#EF4444] rounded-full text-xs font-bold">4</span>
                    <span><strong>Write Exam:</strong> Push boundaries to clear written cutoff.</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-[#EF4444] font-medium">
                    <span className="w-5 h-5 flex items-center justify-center bg-[#EF4444] text-white rounded-full text-xs font-bold">!</span>
                    <span><strong>Suddenly Discover SSB:</strong> Attempt to learn OLQs in 15 days (Result: Rejection).</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/5 flex items-center space-x-2 text-[#EF4444] text-xs font-bold uppercase tracking-wider">
                <X className="h-4 w-4" />
                <span>Leads to wasted attempts and lost age eligibility.</span>
              </div>
            </div>

            {/* Correct Sequence Card */}
            <div className="bg-[#1A2234] rounded-2xl p-6 sm:p-8 border border-[#D4AF37]/45 flex flex-col justify-between shadow-lg shadow-[#D4AF37]/5 relative">
              <div className="absolute top-0 right-6 -translate-y-1/2 bg-[#D4AF37] text-[#0A0F17] text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-wider font-heading shadow-md">
                Proven Success Model
              </div>

              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-xs font-bold text-[#D4AF37] font-heading uppercase">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>The Strategic Path (Highly Guarded)</span>
                </div>
                
                <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading flex items-center gap-2">
                  The Mission Commission™ Sequence
                </h3>
                <p className="text-sm sm:text-base text-gray-300">
                  Build alignment, personality, physical standards, and fundamental academic skills concurrently from Day One.
                </p>

                {/* Flow Block */}
                <div className="space-y-3 bg-[#111827]/80 p-4 rounded-xl border border-[#D4AF37]/25">
                  <div className="flex items-center space-x-3 text-sm text-gray-200">
                    <span className="w-5 h-5 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-bold">1</span>
                    <span><strong>Dream:</strong> Cultivate the aspiration early.</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-200">
                    <span className="w-5 h-5 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-bold">2</span>
                    <span><strong>Understand:</strong> Get full eligibility and entry awareness.</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-200">
                    <span className="w-5 h-5 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-bold">3</span>
                    <span><strong>Choose:</strong> Lock down the most suitable entry route.</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-200">
                    <span className="w-5 h-5 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-bold">4</span>
                    <span><strong>Prepare & Develop:</strong> Start written studies + physical training.</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-[#D4AF37] font-bold">
                    <span className="w-5 h-5 flex items-center justify-center bg-[#D4AF37] text-[#0A0F17] rounded-full text-xs font-bold">5</span>
                    <span><strong>Demonstrate OLQs:</strong> Excel at SSB & clear medicals naturally.</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-white/5 flex items-center space-x-2 text-[#D4AF37] text-xs font-bold uppercase tracking-wider">
                <Check className="h-4 w-4" />
                <span>Transforms confusing hurdles into a predictable blueprint.</span>
              </div>
            </div>

          </div>

          <div className="mt-12">
            <button 
              onClick={() => openModal("Problem Section CTA")}
              className="px-8 py-4 bg-[#D4AF37] text-[#0A0F17] hover:bg-[#E5BE3E] text-sm sm:text-base font-bold rounded-xl uppercase tracking-wider transition-all shadow-md shadow-[#D4AF37]/15 font-subheading"
            >
              Learn the Right Sequence Now
            </button>
          </div>

        </div>
      </section>

      {/* SECTION 3: WHAT WILL I DISCOVER (5-PHASE ROADMAP) */}
      <section id="roadmap" data-testid="roadmap-section" className="py-20 sm:py-28 relative">
        <div className="absolute top-1/2 right-1/4 w-[450px] h-[450px] rounded-full bg-blue-500/[0.02] blur-[110px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="max-w-3xl mx-auto space-y-4 text-center mb-20">
            <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold font-heading">The Strategic Blueprint</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-heading uppercase">
              Your Commission Roadmap <span className="gold-text-gradient">After 12th</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-300">
              The programme helps the aspirant understand the officer-entry landscape appropriate to the candidate's qualifications and current official eligibility requirements, including routes such as NDA/NA and other applicable 10+2 officer-entry opportunities.
            </p>
          </div>

          {/* ROADMAP TIMELINE */}
          <div className="max-w-4xl mx-auto relative border-l-2 border-white/10 ml-4 sm:ml-8 pl-6 sm:pl-12 space-y-12 pb-4">
            
            {/* Step 1 */}
            <div className="relative group">
              {/* Dot */}
              <div className="absolute -left-[31px] sm:-left-[55px] top-1 w-4 sm:w-6 h-4 sm:h-6 rounded-full bg-[#D4AF37] border-4 border-[#0A0F17] group-hover:scale-125 transition-transform" />
              <div className="absolute -left-[31px] sm:-left-[55px] top-1 w-4 sm:w-6 h-4 sm:h-6 rounded-full bg-[#D4AF37]/50 animate-ping pointer-events-none" />
              
              <div className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/25 transition-all">
                <span className="text-xs font-mono font-bold text-[#D4AF37] uppercase tracking-widest bg-[#D4AF37]/10 px-2.5 py-1 rounded">
                  Phase 01
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mt-3 mb-2">
                  Profile & Entry Mapping
                </h3>
                <p className="text-sm sm:text-base text-gray-300">
                  Identify your ideal Armed Forces branch (Army, Navy, Air Force) based on academic streams & medical eligibility. Learn which entries you can apply for and what the exact criteria are.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="absolute -left-[31px] sm:-left-[55px] top-1 w-4 sm:w-6 h-4 sm:h-6 rounded-full bg-white/40 border-4 border-[#0A0F17] group-hover:bg-[#D4AF37] group-hover:scale-125 transition-all" />
              <div className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/25 transition-all">
                <span className="text-xs font-mono font-bold text-gray-400 uppercase tracking-widest bg-white/5 px-2.5 py-1 rounded">
                  Phase 02
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mt-3 mb-2">
                  OLQ (Officer Like Qualities) Ignition
                </h3>
                <p className="text-sm sm:text-base text-gray-300">
                  Systematically build leadership, psychological resilience, stamina, and communication skills from 10th Standard onwards. Cultivate character qualities assessed by the board.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="absolute -left-[31px] sm:-left-[55px] top-1 w-4 sm:w-6 h-4 sm:h-6 rounded-full bg-white/40 border-4 border-[#0A0F17] group-hover:bg-[#D4AF37] group-hover:scale-125 transition-all" />
              <div className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/25 transition-all">
                <span className="text-xs font-mono font-bold text-gray-400 uppercase tracking-widest bg-white/5 px-2.5 py-1 rounded">
                  Phase 03
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mt-3 mb-2">
                  Written Mastery Strategy
                </h3>
                <p className="text-sm sm:text-base text-gray-300">
                  Targeted preparation for NDA/TES without compromising board exam marks. Understand syllabus structures, core subjects, exam patterns, and PYQs.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative group">
              <div className="absolute -left-[31px] sm:-left-[55px] top-1 w-4 sm:w-6 h-4 sm:h-6 rounded-full bg-white/40 border-4 border-[#0A0F17] group-hover:bg-[#D4AF37] group-hover:scale-125 transition-all" />
              <div className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/25 transition-all">
                <span className="text-xs font-mono font-bold text-gray-400 uppercase tracking-widest bg-white/5 px-2.5 py-1 rounded">
                  Phase 04
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mt-3 mb-2">
                  SSB Interview Blueprint
                </h3>
                <p className="text-sm sm:text-base text-gray-300">
                  5-Day SSB protocol decoding: Screening (OIR, PP&DT), GTO ground tasks, Psychological profiling, and Personal Interview. Demystify the assessment criteria.
                </p>
              </div>
            </div>

            {/* Step 5 */}
            <div className="relative group">
              <div className="absolute -left-[31px] sm:-left-[55px] top-1 w-4 sm:w-6 h-4 sm:h-6 rounded-full bg-white/40 border-4 border-[#0A0F17] group-hover:bg-[#D4AF37] group-hover:scale-125 transition-all" />
              <div className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/25 transition-all">
                <span className="text-xs font-mono font-bold text-gray-400 uppercase tracking-widest bg-white/5 px-2.5 py-1 rounded">
                  Phase 05
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mt-3 mb-2">
                  Final Merit List & Academy Joining
                </h3>
                <p className="text-sm sm:text-base text-gray-300">
                  Medical fitness protocols, paperwork guidelines, physical endurance building, and mental readiness for NDA Pune, or INA Ezhimala.
                </p>
              </div>
            </div>

          </div>

          {/* ROADMAP CARD HIGHLIGHTS */}
          <div className="mt-16 bg-[#1A2234] border border-white/5 rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mb-4 text-center">
              The Crucial Questions We Answer Together
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <p className="flex items-start gap-2.5 text-sm text-gray-300"><CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /> Which entries can I apply for post-12th?</p>
                <p className="flex items-start gap-2.5 text-sm text-gray-300"><CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /> What are the official eligibility conditions?</p>
                <p className="flex items-start gap-2.5 text-sm text-gray-300"><CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /> Which service pathway actually suits me?</p>
                <p className="flex items-start gap-2.5 text-sm text-gray-300"><CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /> What subjects & patterns must I prioritize?</p>
              </div>
              <div className="space-y-3">
                <p className="flex items-start gap-2.5 text-sm text-gray-300"><CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /> What does the selection process look like?</p>
                <p className="flex items-start gap-2.5 text-sm text-gray-300"><CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /> What physical and medical standard is mandatory?</p>
                <p className="flex items-start gap-2.5 text-sm text-gray-300"><CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /> What should my next 12–36 months look like?</p>
                <p className="flex items-start gap-2.5 text-sm text-[#D4AF37] font-bold"><CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /> How do I start developing for the SSB now?</p>
              </div>
            </div>
            
            <div className="mt-8 text-center border-t border-white/5 pt-6">
              <button 
                onClick={() => openModal("Roadmap Section CTA")}
                className="px-6 py-3 bg-[#D4AF37] text-[#0A0F17] hover:bg-[#E5BE3E] text-xs sm:text-sm font-bold rounded-lg uppercase tracking-wider font-subheading"
              >
                Claim Your Custom Roadmap
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 4: THE REAL ADVANTAGE OF STARTING EARLY */}
      <section className="py-20 sm:py-28 bg-[#111827] relative border-t border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/[0.015] to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="max-w-3xl mx-auto space-y-4 text-center mb-16">
            <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold font-heading">The Early Edge</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-heading uppercase">
              Don't Just Prepare for NDA.<br />
              <span className="gold-text-gradient">Start Developing for Officership.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-300">
              A Class 11 or 12 student possesses something extremely valuable: <strong>TIME TO DEVELOP</strong>. You do not build "Officer Like Qualities" in 15 days of crash-coaching. Development takes structured discipline.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Advantage Stat Card */}
            <div className="bg-[#0A0F17] border border-white/5 rounded-2xl p-6 flex flex-col justify-between md:col-span-2 lg:col-span-1">
              <div className="space-y-4">
                <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold font-heading">Strong Proposition</span>
                <p className="text-2xl sm:text-3xl font-bold text-white font-heading leading-tight uppercase">
                  "You don't become officer-like in 15 days. Start developing before you start performing."
                </p>
                <p className="text-sm text-gray-400">
                  SSB assessors can instantly spot candidates who have crammed answers. Our focus is genuine development.
                </p>
              </div>
              <div className="pt-6 border-t border-white/5 mt-6 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Mentorship Approach</p>
                  <p className="text-sm font-bold text-white">Genuine OLQ Growth</p>
                </div>
                <div className="p-2 bg-[#D4AF37]/10 rounded-full">
                  <Shield className="h-5 w-5 text-[#D4AF37]" />
                </div>
              </div>
            </div>

            {/* Grid list of things to develop */}
            <div className="bg-[#1A2234] border border-[#D4AF37]/20 rounded-2xl p-6 sm:p-8 md:col-span-2 space-y-6">
              <h3 className="text-lg sm:text-xl font-bold text-white font-subheading flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-[#D4AF37]" />
                What can you master with an early 12-36 month headstart?
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Time to Read</p>
                  <p className="text-xs text-gray-400 mt-1">Deepen general knowledge</p>
                </div>
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Communication</p>
                  <p className="text-xs text-gray-400 mt-1">Fluent, confident speech</p>
                </div>
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Fitness & Sports</p>
                  <p className="text-xs text-gray-400 mt-1">Stamina, strength & speed</p>
                </div>
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Public Speaking</p>
                  <p className="text-xs text-gray-400 mt-1">Commanding presence</p>
                </div>
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Teamwork</p>
                  <p className="text-xs text-gray-400 mt-1">Cooperate & lead groups</p>
                </div>
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Responsibility</p>
                  <p className="text-xs text-gray-400 mt-1">Accountable execution</p>
                </div>
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Self-Confidence</p>
                  <p className="text-xs text-gray-400 mt-1">Unshakeable conviction</p>
                </div>
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Learn setback</p>
                  <p className="text-xs text-gray-400 mt-1">Rise from failures</p>
                </div>
                <div className="p-3.5 bg-[#111827]/80 rounded-xl border border-white/5 text-center sm:text-left hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-bold text-white">Discipline</p>
                  <p className="text-xs text-gray-400 mt-1">Daily structured routine</p>
                </div>
              </div>
            </div>

          </div>

          {/* Quick Stats banner */}
          <div className="mt-12 bg-[#1A2234]/70 rounded-2xl p-6 border border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6 max-w-5xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#D4AF37]/10 rounded-xl">
                <TrendingUp className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-sm font-bold text-white">Secure Peak Selection Window</p>
                <p className="text-xs text-gray-400">Join the elite few who plan while others search</p>
              </div>
            </div>
            <button
              onClick={() => openModal("Advantages Section CTA")}
              className="px-6 py-3 bg-[#D4AF37] text-[#0A0F17] hover:bg-[#E5BE3E] text-xs sm:text-sm font-bold rounded-lg uppercase tracking-wider font-subheading whitespace-nowrap"
            >
              Start Early Advantage Now
            </button>
          </div>

        </div>
      </section>

      {/* SECTION 5: INTRODUCE THE OFFICER PATH™ */}
      <section id="officer-path" className="py-20 sm:py-28 relative">
        <div className="absolute top-1/3 left-10 w-[350px] h-[350px] rounded-full bg-[#D4AF37]/[0.015] blur-[90px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="max-w-3xl mx-auto space-y-4 text-center mb-20">
            <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold font-heading">The Core Framework</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-heading uppercase">
              One Dream. <span className="gold-text-gradient">One Structured Development Path.</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-300">
              The proprietary <strong>OFFICER Path™</strong> breaks down the military leadership development process into seven clear, actionable steps.
            </p>
          </div>

          {/* 7 PILLARS GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            
            {/* O */}
            <div data-testid="officer-path-card" className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/35 transition-all relative overflow-hidden group">
              <div className="absolute top-4 right-4 text-6xl font-black font-heading text-white/5 select-none group-hover:text-[#D4AF37]/10 transition-colors">O</div>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-heading text-xl font-bold text-[#D4AF37]">
                  O
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-subheading">Orientation</h3>
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded">Outcome: Clarity</span>
                </div>
              </div>
              <p className="text-sm text-gray-300">
                Choose the right entry path, understand exact eligibility parameters, and map out your custom career timeline.
              </p>
            </div>

            {/* F */}
            <div data-testid="officer-path-card" className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/35 transition-all relative overflow-hidden group">
              <div className="absolute top-4 right-4 text-6xl font-black font-heading text-white/5 select-none group-hover:text-[#D4AF37]/10 transition-colors">F</div>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-heading text-xl font-bold text-[#D4AF37]">
                  F
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-subheading">Foundation</h3>
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">Outcome: Competence</span>
                </div>
              </div>
              <p className="text-sm text-gray-300">
                Build Mathematics, English, GK, Current Affairs, and reasoning fundamentals appropriate to your defence entry.
              </p>
            </div>

            {/* F */}
            <div data-testid="officer-path-card" className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/35 transition-all relative overflow-hidden group">
              <div className="absolute top-4 right-4 text-6xl font-black font-heading text-white/5 select-none group-hover:text-[#D4AF37]/10 transition-colors">F</div>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-heading text-xl font-bold text-[#D4AF37]">
                  F
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-subheading">Fitness</h3>
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">Outcome: Readiness</span>
                </div>
              </div>
              <p className="text-sm text-gray-300">
                Develop strong physical stamina, endurance, healthy body parameters, through awareness of medical guidelines.
              </p>
            </div>

            {/* I */}
            <div data-testid="officer-path-card" className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/35 transition-all relative overflow-hidden group">
              <div className="absolute top-4 right-4 text-6xl font-black font-heading text-white/5 select-none group-hover:text-[#D4AF37]/10 transition-colors">I</div>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-heading text-xl font-bold text-[#D4AF37]">
                  I
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-subheading">Identity</h3>
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded">Outcome: Confidence</span>
                </div>
              </div>
              <p className="text-sm text-gray-300">
                Sharpen confidence, clear public communication, self-awareness, active vocabulary, and daily military habits.
              </p>
            </div>

            {/* C */}
            <div data-testid="officer-path-card" className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/35 transition-all relative overflow-hidden group">
              <div className="absolute top-4 right-4 text-6xl font-black font-heading text-white/5 select-none group-hover:text-[#D4AF37]/10 transition-colors">C</div>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-heading text-xl font-bold text-[#D4AF37]">
                  C
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-subheading">Character</h3>
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Outcome: Leadership</span>
                </div>
              </div>
              <p className="text-sm text-gray-300">
                Develop integrity, high responsibility, team cooperation, fast logical judgement, and leadership skills.
              </p>
            </div>

            {/* E */}
            <div data-testid="officer-path-card" className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/35 transition-all relative overflow-hidden group">
              <div className="absolute top-4 right-4 text-6xl font-black font-heading text-white/5 select-none group-hover:text-[#D4AF37]/10 transition-colors">E</div>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-heading text-xl font-bold text-[#D4AF37]">
                  E
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-subheading">Evaluation</h3>
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">Outcome: Potential</span>
                </div>
              </div>
              <p className="text-sm text-gray-300">
                Progressively master and prepare for OIR, PP&DT tasks, psychology tests, GTO tasks, and mock personal interviews.
              </p>
            </div>

          </div>

          {/* Centered R card */}
          <div className="mt-6 flex justify-center max-w-6xl mx-auto">
            <div data-testid="officer-path-card" className="bg-[#111827]/60 border border-white/5 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/35 transition-all relative overflow-hidden group w-full md:max-w-md">
              <div className="absolute top-4 right-4 text-6xl font-black font-heading text-white/5 select-none group-hover:text-[#D4AF37]/10 transition-colors">R</div>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-heading text-xl font-bold text-[#D4AF37]">
                  R
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-subheading">Ready for Commission</h3>
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded">Outcome: Commissioning</span>
                </div>
              </div>
              <p className="text-sm text-gray-300">
                Prepare for medical clearances, absolute paperwork accuracy, joining protocols, and initial academy life.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 6: THREE-LEVEL OFFER */}
      <section id="offers" className="py-20 sm:py-28 bg-[#111827] relative border-t border-b border-white/5">
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-blue-500/[0.015] blur-[120px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="max-w-3xl mx-auto space-y-4 text-center mb-16">
            <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold font-heading">Your Options</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-heading uppercase">
              Choose Your <span className="gold-text-gradient">Mentorship Level</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-300">
              Select the structured framework that corresponds directly to your preparation, ambitions, and timing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch max-w-6xl mx-auto">
            
            {/* Tier 1 */}
            <div data-testid="tier-card-1" className="bg-[#0A0F17] rounded-2xl p-6 sm:p-8 border border-white/5 flex flex-col justify-between hover:border-[#D4AF37]/20 transition-all">
              <div className="space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold font-heading">L1 Discovery</span>
                  <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mt-1">
                    Officer Path™ Discovery
                  </h3>
                  <p className="text-xs text-gray-400 font-medium mt-1">Know Your Path.</p>
                </div>

                <div className="py-4 border-t border-b border-white/5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl sm:text-4xl font-extrabold text-white font-heading">₹999</span>
                    <span className="text-sm text-gray-500 line-through">₹2,999</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Transformation: Confused Student → Informed Aspirant</p>
                </div>

                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Commission routes & options after 12th</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Accurate Eligibility analysis & mapping</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Written Exam patterns & structures</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Medical and Physical parameters</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Personal Commission Roadmap™ PDF</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => openModal("Level 1 Foundation Starter")}
                  data-testid="tier-book-seat-btn-1"
                  className="w-full py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-colors font-subheading"
                >
                  Get Foundation Access
                </button>
              </div>
            </div>

            {/* Tier 2 (Popular) */}
            <div data-testid="tier-card-2" className="bg-[#1A2234] rounded-2xl p-6 sm:p-8 border border-[#D4AF37]/50 flex flex-col justify-between shadow-xl shadow-[#D4AF37]/5 relative transform scale-100 lg:scale-105">
              <div className="absolute top-0 right-6 -translate-y-1/2 bg-[#D4AF37] text-[#0A0F17] text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-wider font-heading shadow-md">
                RECOMMENDED / MOST POPULAR
              </div>

              <div className="space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold font-heading">L2 Mastery</span>
                  <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mt-1">
                    Mastery to Open Multiple Paths: NDA/NA, TES and Navy 10+2 BTech entry
                  </h3>
                  <p className="text-xs text-gray-300 font-medium mt-1">Open multiple paths while retaining options to pursue Engineering in civil.</p>
                </div>

                <div className="py-4 border-t border-b border-[#D4AF37]/20 text-left">
                  <div className="inline-block px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/35 rounded-md text-xs font-bold uppercase tracking-wider text-[#D4AF37] font-heading mb-2">
                    Exclusive Course Admission
                  </div>
                  <p className="text-xs text-gray-300 mt-1">This course cost is exclusively for exam mastery and does NOT include Level 1 (L1).</p>
                  <p className="text-xs text-gray-300 mt-1">Transformation: Informed Aspirant → Exam-Ready Candidate</p>
                </div>

                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-xs text-gray-200">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Multiple Pathways: NDA/NA, TES & Navy 10+2 BTech</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-200">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Retain option to pursue Civil Engineering</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-200">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Structured Syllabus prep blueprints & PYQs</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-200">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Concept mastery, Mock examinations</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-200">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Mission Error Log™ & Final Approach 21™ implementation</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => openModal("Level 2 Officer Mastery")}
                  data-testid="tier-book-seat-btn-2"
                  className="w-full py-4 rounded-xl text-xs font-bold uppercase tracking-wider bg-[#D4AF37] text-[#0A0F17] hover:bg-[#E5BE3E] transition-all transform hover:scale-[1.02] shadow-md shadow-[#D4AF37]/20 font-subheading"
                >
                  Book Officer Mastery Seat
                </button>
              </div>
            </div>

            {/* Tier 3 */}
            <div data-testid="tier-card-3" className="bg-[#0A0F17] rounded-2xl p-6 sm:p-8 border border-white/5 flex flex-col justify-between hover:border-[#D4AF37]/20 transition-all">
              <div className="space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold font-heading">L3 Elite</span>
                  <h3 className="text-xl sm:text-2xl font-bold text-white font-subheading mt-1">
                    Officer Development & SSB Mastery™
                  </h3>
                  <p className="text-xs text-gray-400 font-medium mt-1">Develop the Person. Demonstrate Potential.</p>
                </div>

                <div className="py-4 border-t border-b border-white/5 text-left">
                  <div className="inline-block px-3 py-1 bg-white/5 border border-white/15 rounded-md text-xs font-bold uppercase tracking-wider text-white font-heading mb-2">
                    Elite Level Admission
                  </div>
                  <p className="text-xs text-gray-400 mt-1">This exclusive 1-on-1 personal mentorship course does NOT include L1 or L2.</p>
                  <p className="text-xs text-gray-400 mt-1">Transformation: Aspirant → Developed & Assessment-Ready Candidate</p>
                </div>

                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Full Self-awareness & Communication coach</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Officer mindset & daily routine blueprints</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Psychology, GTO, Personal Mock SSBs</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>1-on-1 Personal Strategy Call with Col Ravindranath Tiwari</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>Parent Career consultation access</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => openModal("Level 3 Elite Command Mentorship")}
                  data-testid="tier-book-seat-btn-3"
                  className="w-full py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-colors font-subheading"
                >
                  Claim Elite Command Seat
                </button>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* SECTION 7: PARENT SECTION */}
      <section className="py-20 sm:py-28 relative">
        <div className="absolute top-1/2 left-1/4 w-[350px] h-[350px] rounded-full bg-blue-500/[0.01] blur-[80px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-[#1A2234] border border-[#D4AF37]/35 rounded-2xl p-6 sm:p-12 max-w-5xl mx-auto relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-[#D4AF37]/5 rounded-bl-full pointer-events-none" />
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
              
              <div className="lg:col-span-4 flex flex-col justify-center items-center lg:items-start text-center lg:text-left space-y-3">
                <div className="p-3 bg-[#D4AF37]/10 rounded-2xl border border-[#D4AF37]/20">
                  <Heart className="h-8 w-8 text-[#D4AF37] fill-[#D4AF37]/10" />
                </div>
                <h3 className="text-xl uppercase tracking-wider text-[#D4AF37] font-extrabold font-heading">A Special Message</h3>
                <p className="text-2xl sm:text-3xl font-bold text-white font-heading uppercase leading-tight">
                  For Concerned Parents
                </p>
              </div>

              <div className="lg:col-span-8 space-y-6 text-left border-t lg:border-t-0 lg:border-l border-white/10 pt-6 lg:pt-0 lg:pl-8">
                <p className="text-lg sm:text-xl font-bold text-white font-subheading italic leading-relaxed">
                  "Parents: Don't Just Ask, 'Which Coaching Should My Child Join?' Ask instead: 'What kind of young adult should my child become over the next three years?'"
                </p>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed font-body">
                  The Armed Forces journey is a magnificent developmental milestone that shapes unshakeable character, steel discipline, fit bodies, fluent communication, and lifetime leadership status. Even beyond clearing examinations, the value instilled by Mission Commission™ shapes capabilities valuable for military selection — and for life itself. We guide your children side-by-side to make you proud.
                </p>
                
                <div className="flex flex-wrap gap-2 pt-2 text-xs font-bold uppercase tracking-widest text-[#D4AF37] font-heading">
                  <span>Discipline</span> • <span>Fitness</span> • <span>Communication</span> • <span>Responsibility</span> • <span>Confidence</span> • <span>Teamwork</span> • <span>Character</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: MENTOR SECTION */}
      <section id="mentor" className="py-20 sm:py-28 bg-[#111827] relative border-t border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-5xl mx-auto">
            
            {/* Mentor Image */}
            <div className="lg:col-span-5 relative">
              <div className="relative max-w-[360px] mx-auto rounded-2xl overflow-hidden border-2 border-[#D4AF37] gold-glow">
                <img 
                  src="https://tagmango.com/assets/photo-ravi-885df2d18b25d0b1b3590668ee2fa35d.jpeg" 
                  alt="Col Ravindranath Tiwari (Retd) - Senior Defence Mentor and Military Leadership Strategist" 
                  data-testid="mentor-image"
                  className="w-full h-auto object-cover transform hover:scale-[1.02] transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F17] via-transparent to-transparent opacity-30 pointer-events-none" />
              </div>
              
              <div className="absolute -bottom-4 right-4 bg-[#D4AF37] text-[#0A0F17] px-4 py-2 rounded-lg text-xs font-extrabold uppercase tracking-widest font-heading shadow-lg">
                VETERAN LEADER
              </div>
            </div>

            {/* Mentor Copy */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="space-y-2">
                <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold font-heading">Lead Strategist & Instructor</span>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white font-heading uppercase">
                  Col Ravindranath Tiwari (Retd)
                </h2>
                <p className="text-sm text-[#D4AF37] font-bold tracking-wider font-subheading uppercase">
                  MTech, IIT Madras • Indian Army Veteran • Personality & Officer Mentor
                </p>
              </div>

              <blockquote className="border-l-4 border-[#D4AF37] pl-4 italic text-base sm:text-lg text-gray-200">
                “My purpose is not merely to tell young people how to answer an SSB question. It is to help them start developing into the kind of young men and women who can shoulder immense national responsibility.”
              </blockquote>

              <p className="text-sm sm:text-base text-gray-300 leading-relaxed font-body">
                With over three decades of highly distinguished service in the Indian Army, Col Ravindranath Tiwari (Retd) has leadership experience in command of troops at various levels and in challenging conditions, has mentored students at undergraduate level and candidates aspiring to be commissioned into the armed forces, and has designed officer-readiness training modules. Under 'Mission Commission', his objective is to empower 12th-standard aspirants with unshakeable military clarity, develop their confidence and forge a commissioned ready persona.
              </p>

              <div className="space-y-3 bg-[#0A0F17]/60 p-5 rounded-xl border border-white/5">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-bold font-heading">Key Career Milestones</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-300">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                    <span>30+ Years Active Army Service</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                    <span>IIT Madras Alumnus (MTech)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
                    <span>Mentored and guided UPSC aspirants</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-24 sm:py-32 relative text-center overflow-hidden">
        {/* Glowing backgrounds */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/5 blur-[130px] pointer-events-none" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-xs font-bold text-[#D4AF37] font-heading uppercase">
            <Target className="h-4 w-4" />
            <span>Enrollment Open For Next Cohort</span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight font-heading uppercase">
            START YOUR OFFICER <span className="gold-text-gradient">JOURNEY EARLY</span>
          </h2>

          <p className="text-lg sm:text-xl font-medium text-gray-300 font-subheading max-w-2xl mx-auto">
            Know Your Path. Build Your Foundation. Develop Yourself. Make Your Family and Country Proud.
          </p>

          <div className="pt-4">
            <button
              onClick={() => openModal("Final CTA Button")}
              data-testid="final-cta-btn"
              className="px-10 py-5 bg-[#D4AF37] hover:bg-[#E5BE3E] text-[#0A0F17] text-sm sm:text-lg font-extrabold rounded-xl uppercase tracking-widest transition-all transform hover:-translate-y-0.5 shadow-xl shadow-[#D4AF37]/30 font-subheading inline-flex items-center gap-3.5"
            >
              Discover My Commission Route After 12th
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-gray-400 pt-6">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Instant Access</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> 100% Satisfaction Guaranteed</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Secure SSL Server Connection</span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="mt-auto bg-[#0A0F17] border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <div>
            <span className="font-heading font-black text-lg tracking-wider text-white">
              MISSION COMMISSION<span className="text-[#D4AF37]">™</span>
            </span>
            <p className="text-xs text-gray-500 mt-1">
              &copy; 2026 Mission Commission. All rights reserved. Branded domain: acquire.missioncommision.com
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* MANGO AI FOOTER BADGE */}
            <div 
              data-testid="mango-ai-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-md text-[10px] text-gray-400 font-mono tracking-wide uppercase font-bold"
            >
              <Sparkles className="h-3 w-3 text-[#D4AF37]" />
              <span>Powered by Mango AI (from TagMango Mango Studio)</span>
            </div>
          </div>
        </div>
      </footer>

      {/* INTERACTIVE LEAD modal / POPUP */}
      {showLeadModal && (
        <div 
          data-testid="lead-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md transition-all animate-in fade-in"
        >
          {/* Modal box */}
          <div className="relative w-full max-w-md bg-[#111827] border border-[#D4AF37]/50 rounded-2xl p-6 sm:p-8 shadow-2xl gold-glow overflow-hidden">
            
            {/* Floating absolute backdrop */}
            <div className="absolute top-0 right-0 w-[150px] h-[150px] bg-[#D4AF37]/5 rounded-bl-full pointer-events-none" />
            
            {/* Close button */}
            <button 
              onClick={closeModal}
              data-testid="lead-modal-close"
              className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>

            {!isSubmitted ? (
              // Form State
              <form onSubmit={handleFormSubmit} noValidate className="space-y-5">
                <div className="space-y-1.5 text-left">
                  <span className="text-[10px] font-bold uppercase font-mono tracking-widest text-[#D4AF37]">
                    Selected Priority: {selectedTier}
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold text-white font-heading uppercase">
                    Reserve Your Seat Early
                  </h3>
                  <p className="text-xs text-gray-400">
                    Provide your credentials below to immediately secure discounted seats with Col Ravindranath Tiwari's office.
                  </p>
                </div>

                {errorMsg && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-[#EF4444] flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <div className="space-y-4 text-left">
                  {/* Full Name */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-300" htmlFor="fullName">
                      Full Name
                    </label>
                    <input 
                      type="text"
                      id="fullName"
                      name="fullName"
                      data-testid="lead-input-name"
                      placeholder="e.g. Rahul Sharma"
                      required
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#0A0F17] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-300" htmlFor="email">
                      Email Address
                    </label>
                    <input 
                      type="email"
                      id="email"
                      name="email"
                      data-testid="lead-input-email"
                      placeholder="e.g. rahul@gmail.com"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#0A0F17] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>

                  {/* WhatsApp Phone */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-300" htmlFor="phone">
                      Mobile / WhatsApp Number
                    </label>
                    <input 
                      type="tel"
                      id="phone"
                      name="phone"
                      data-testid="lead-input-phone"
                      placeholder="e.g. 9876543210"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#0A0F17] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>

                  {/* Current Academic Status */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-300" htmlFor="academicStatus">
                      Current Stream / Academic Status
                    </label>
                    <select 
                      id="academicStatus"
                      name="academicStatus"
                      data-testid="lead-input-status"
                      required
                      value={formData.academicStatus}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#0A0F17] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    >
                      <option value="" disabled>Select status...</option>
                      <option value="Class 11th">Class 11th Student</option>
                      <option value="Class 12th (Appearing)">Class 12th Student (Appearing)</option>
                      <option value="12th Passed">12th Standard Passed</option>
                      <option value="Parent of Aspirant">Concerned Parent</option>
                    </select>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    data-testid="lead-submit-btn"
                    className="w-full py-4 rounded-xl text-xs sm:text-sm font-bold uppercase tracking-widest bg-[#D4AF37] text-[#0A0F17] hover:bg-[#E5BE3E] transition-all transform hover:scale-[1.01] shadow-lg shadow-[#D4AF37]/20 font-subheading flex items-center justify-center gap-2"
                  >
                    Confirm & Proceed to Secure Seat
                    <ArrowRight className="h-4.5 w-4.5" />
                  </button>
                </div>
              </form>
            ) : (
              // Success / Ticket confirmation state
              <div data-testid="lead-success-msg" className="space-y-6 text-center py-4">
                <div className="inline-flex p-3 bg-emerald-500/10 rounded-full border border-emerald-500/30 text-emerald-400 mb-2">
                  <Check className="h-8 w-8" />
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-white font-heading uppercase tracking-wider">
                    Seat Blocked!
                  </h3>
                  <p className="text-sm text-gray-300">
                    Welcome to the elite cohort. Your credentials have been successfully registered under <strong>Mission Commission™</strong>.
                  </p>
                </div>

                {/* Ticket Receipt design */}
                <div className="bg-[#0A0F17] border border-[#D4AF37]/35 rounded-xl p-4 text-left font-mono text-xs space-y-2 relative">
                  <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#D4AF37]/15 text-[#D4AF37] text-[10px] font-bold uppercase rounded">
                    PROVISIONAL
                  </div>
                  <p className="text-gray-500 border-b border-white/5 pb-2 text-[10px] uppercase font-bold tracking-wider">
                    Priority Entry Confirmation
                  </p>
                  <p><span className="text-gray-500">Ticket ID:</span> <span className="text-white font-bold">{ticketId}</span></p>
                  <p><span className="text-gray-500">Aspirant:</span> <span className="text-white font-bold">{formData.fullName}</span></p>
                  <p><span className="text-gray-500">Academic:</span> <span className="text-white font-bold">{formData.academicStatus}</span></p>
                  <p><span className="text-gray-500">Priority:</span> <span className="text-[#D4AF37] font-bold uppercase">{selectedTier}</span></p>
                </div>

                {/* WhatsApp call to action warning */}
                <div className="p-3 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl flex items-start gap-3 text-left">
                  <Phone className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-white uppercase tracking-wider">WhatsApp Onboarding</p>
                    <p className="text-[11px] text-gray-400 leading-normal mt-0.5">
                      Col Ravindranath Tiwari's office will ping you on <strong>{formData.phone}</strong> within 12 hours with joining guidelines. Keep your mobile ready.
                    </p>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    onClick={closeModal}
                    className="px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-colors"
                  >
                    Close Window
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
}
